import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { Accrual } from '../../../../models/api/view/claim/hr/accruals/accrual';
import { ValidatorsEx } from '../../../../validators/validators-ex';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { ViewService } from '../../../../services/view/view.service';

@Component({
  selector: 'app-accruals',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    RangeComponent,
    ButtonComponent,
  ],
  template: `
    <app-page-header label="Accruals" />
    <div class="tw-flex tw-gap-4 tw-items-center">
      <div>
        <app-range
          right1
          [control]="fg.controls.beginDate"
          [toControl]="fg.controls.endDate"
          [maxSpanDays]="null"
          label="Date range"
        />
        @if (
          fg.invalid &&
          (fg.dirty || fg.touched) &&
          fg.errors?.['bothOrNoneRequired']
        ) {
          <div
            class="tw-text-[var(--sed-status-red-color)] tw-font-bold tw-text-[80%] tw-px-4 tw-pt-1 tw-pb-2 tw-leading-[14px] app-fade-in"
          >
            Error: {{ fg.errors['bothOrNoneRequired'] }}
          </div>
        }
      </div>
      <app-button
        label="Search"
        type="submit"
        (click)="searchClick()"
        [disabled]="!fg.valid"
      />
    </div>
    <app-table
      [dataSource]="accruals"
      [pageSize]="10"
      [scrollable]="false"
      dataKey="id"
      recordLabel="Accrual"
    >
      <app-column name="type" label="Accrual type" />
      <app-column name="dateEffective" label="Effective Date" format="d" />
      <app-column name="unit" label="Unit" />
      <app-column name="value" label="Value" />
      <app-column name="frequency" label="Frequency" />
      <app-column name="amount" label="Amount" format="c" />
    </app-table>
  `,
  styles: ``,
})
export class AccrualsComponent extends BaseComponent implements OnInit {
  private viewService = inject(ViewService);

  accruals: Accrual[] = [];
  claim: any;

  fg = new FormGroup(
    {
      beginDate: new FormControl<Date>(null),
      endDate: new FormControl<Date>(null),
    },
    [
      ValidatorsEx.bothOrNoneRequired(
        'beginDate',
        'endDate',
        'Both dates are required or both should be empty',
      ),
    ],
  );

  async searchClick() {
    this.fg.markAllAsTouched();
    if (this.fg.valid) {
      // Convert dates to ISO date strings (YYYY-MM-DD) to prevent timezone issues
      const beginDateStr =
        this.fg.value.beginDate?.toISOString().split('T')[0] || null;
      const endDateStr =
        this.fg.value.endDate?.toISOString().split('T')[0] || null;

      this.accruals = await this.api.accruals.get(
        getClaimKey(),
        beginDateStr,
        endDateStr,
      );
    }
  }

  async ngOnInit() {
    this.claim = this.viewService.currentClaim();
    const beginDateStr = this.getDefaultDate(this.claim)
      .toISOString()
      .split('T')[0];
    const endDateStr = new Date().toISOString().split('T')[0];
    this.fg.setValue({
      beginDate: new Date(this.getDefaultDate(this.claim)),
      endDate: new Date(),
    });
    this.accruals = await this.api.accruals.get(
      getClaimKey(),
      beginDateStr,
      endDateStr,
    );
    this.view.hideLoading();
  }

  getDefaultDate(claim: any) {
    if (claim?.dateOfLoss) {
      return new Date(claim.dateOfLoss);
    } else {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
  }
}
